// src/contentfulClient.ts
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';

import { getCategoryForPost, type CategorySlug } from '@/lib/categories';
import { captureException } from '@/lib/sentry';

export type Author = {
  id: string;
  name: string;
  slug: string;
};

export type Post = {
  id: string;
  title: string;
  slug: string;
  subtitle?: string; // Long text subtitle/description
  excerpt?: string;
  body?: string; // HTML formatted body
  thumbnailUrl?: string; // resolved asset URL
  category?: string;
  tags?: string[];
  date?: string;
  featured?: boolean;
  featuredOrder?: number;
  author?: Author; // Author reference from Contentful
};

// API endpoints - using our backend instead of direct Contentful calls
const API_BASE = '/api';

function buildAssetMap(data: any): Record<string, string> {
  const assets: Record<string, string> = {};
  if (data.includes && Array.isArray(data.includes.Asset)) {
    for (const asset of data.includes.Asset) {
      const id = asset.sys?.id;
      const url = asset.fields?.file?.url;
      if (id && url) {
        assets[id] = url.startsWith('//') ? `https:${url}` : url;
      }
    }
  }
  return assets;
}

function buildEntryMap(data: any): Record<string, any> {
  const entries: Record<string, any> = {};
  if (data.includes && Array.isArray(data.includes.Entry)) {
    for (const entry of data.includes.Entry) {
      const id = entry.sys?.id;
      if (id) {
        entries[id] = entry;
      }
    }
  }
  return entries;
}

function parseAuthor(authorRef: any, entries: Record<string, any>): Author | undefined {
  if (!authorRef || !authorRef.sys?.id) return undefined;

  const authorEntry = entries[authorRef.sys.id];
  if (!authorEntry || !authorEntry.fields) return undefined;

  const fields = authorEntry.fields;
  const name = fields.name || fields.title || '';

  // Generate slug from name if not provided
  const slug =
    fields.slug ||
    name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[áàäâ]/g, 'a')
      .replace(/[éèëê]/g, 'e')
      .replace(/[íìïî]/g, 'i')
      .replace(/[óòöôő]/g, 'o')
      .replace(/[úùüûű]/g, 'u')
      .replace(/[^a-z0-9-]/g, '');

  return {
    id: authorEntry.sys.id,
    name,
    slug,
  };
}

function richTextToHtml(doc: any): string {
  if (!doc || !doc.nodeType) return '';
  try {
    const options = {
      renderMark: {
        [MARKS.BOLD]: (text: string) => `<strong>${text}</strong>`,
        [MARKS.ITALIC]: (text: string) => `<em>${text}</em>`,
        [MARKS.UNDERLINE]: (text: string) => `<u>${text}</u>`,
        [MARKS.CODE]: (text: string) => `<code>${text}</code>`,
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node: any, next: any) => `<p>${next(node.content)}</p>`,
        [BLOCKS.HEADING_1]: (node: any, next: any) => `<h1>${next(node.content)}</h1>`,
        [BLOCKS.HEADING_2]: (node: any, next: any) => `<h2>${next(node.content)}</h2>`,
        [BLOCKS.HEADING_3]: (node: any, next: any) => `<h3>${next(node.content)}</h3>`,
        [BLOCKS.HEADING_4]: (node: any, next: any) => `<h4>${next(node.content)}</h4>`,
        [BLOCKS.HEADING_5]: (node: any, next: any) => `<h5>${next(node.content)}</h5>`,
        [BLOCKS.HEADING_6]: (node: any, next: any) => `<h6>${next(node.content)}</h6>`,
        [BLOCKS.UL_LIST]: (node: any, next: any) => `<ul>${next(node.content)}</ul>`,
        [BLOCKS.OL_LIST]: (node: any, next: any) => `<ol>${next(node.content)}</ol>`,
        [BLOCKS.LIST_ITEM]: (node: any, next: any) => `<li>${next(node.content)}</li>`,
        // No classes on the elements: the article styles live in `.blog-content` (src/index.css)
        [BLOCKS.QUOTE]: (node: any, next: any) => `<blockquote>${next(node.content)}</blockquote>`,
        [BLOCKS.HR]: () => `<hr />`,
        [INLINES.HYPERLINK]: (node: any, next: any) => {
          const uri = node.data?.uri || '#';
          return `<a href="${uri}" target="_blank" rel="noopener noreferrer">${next(node.content)}</a>`;
        },
      },
    };
    return documentToHtmlString(doc, options);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('Error converting rich text to HTML:', err);
    }
    return '';
  }
}

function parseCategory(rawCategory: any): string {
  if (!rawCategory) return '';

  if (typeof rawCategory === 'string') {
    return rawCategory;
  }

  if (Array.isArray(rawCategory)) {
    const firstCategory = rawCategory.find((value) => typeof value === 'string');
    if (firstCategory) return firstCategory;
  }

  if (typeof rawCategory === 'object') {
    return rawCategory.fields?.name ?? rawCategory.fields?.title ?? '';
  }

  return '';
}

function parseTags(rawTags: any): string[] {
  if (!rawTags) return [];

  if (Array.isArray(rawTags)) {
    return rawTags
      .map((tag) => (typeof tag === 'string' ? tag : typeof tag?.name === 'string' ? tag.name : ''))
      .filter(Boolean);
  }

  if (typeof rawTags === 'string') {
    return rawTags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
}

function mapEntryToPost(
  entry: any,
  assets: Record<string, string>,
  entries: Record<string, any> = {}
): Post {
  const fields = entry.fields || {};

  const thumbnailRef = fields.featuredImage || fields.thumbnail;
  const thumbnailUrl =
    thumbnailRef && thumbnailRef.sys && thumbnailRef.sys.id
      ? assets[thumbnailRef.sys.id]
      : undefined;

  const bodyField = fields.body ?? fields.content;
  let body: string | undefined;

  if (typeof bodyField === 'string') {
    body = bodyField;
  } else if (bodyField && typeof bodyField === 'object') {
    body = richTextToHtml(bodyField);
  }

  // Handle subtitle field - try multiple field names and handle both string and rich text
  // Try various field name variations including capitalized versions
  const subtitleField =
    fields.subtitle ??
    fields.subTitle ??
    fields.Subtitle ??
    fields.description ??
    fields.intro ??
    fields.summary;
  let subtitle: string | undefined;

  if (typeof subtitleField === 'string') {
    subtitle = subtitleField;
  } else if (subtitleField && typeof subtitleField === 'object') {
    subtitle = richTextToHtml(subtitleField);
  }

  // Parse author reference
  const author = parseAuthor(fields.author, entries);

  return {
    id: entry.sys?.id ?? '',
    title: fields.title ?? '',
    slug: fields.slug ?? '',
    subtitle: subtitle ?? '',
    excerpt: fields.excerpt ?? '',
    body: body ?? '',
    thumbnailUrl,
    category: parseCategory(fields.category),
    tags: parseTags(fields.tags),
    date: entry.sys?.createdAt ?? '',
    featured: fields.featured === true,
    featuredOrder: typeof fields.featuredOrder === 'number' ? fields.featuredOrder : undefined,
    author,
  };
}

// Retry transient API failures (network error, 429, 5xx) before giving up
const RETRY_DELAYS_MS = [700, 2000];

const USER_FACING_ERROR =
  'A tartalom betöltése átmenetileg nem sikerült. Automatikusan újrapróbáljuk…';

export class ContentApiError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ContentApiError';
    this.status = status;
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetch JSON from our backend API with retries.
 *
 * Returns the parsed body.
 * Throws ContentApiError (with a user-facing message) when every attempt failed;
 * the failure is reported to Sentry as an error, so outages are visible.
 */
async function fetchApiJson(url: string, context: Record<string, unknown>): Promise<any> {
  let lastStatus: number | undefined;
  let lastReason = 'unknown';

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1]);

    try {
      const res = await fetch(url);
      if (res.ok) return await res.json();

      lastStatus = res.status;
      lastReason = `HTTP ${res.status} ${res.statusText}`;
      // Client errors (except rate limiting) won't fix themselves - don't retry
      if (res.status < 500 && res.status !== 429) break;
    } catch (error) {
      lastStatus = undefined;
      lastReason = error instanceof Error ? error.message : String(error);
    }
  }

  const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  // A visitor without internet is not a site outage - don't spam monitoring with it
  if (online) {
    captureException(
      new ContentApiError(`Content API unavailable: ${url} (${lastReason})`, lastStatus),
      {
        ...context,
        url,
        status: lastStatus,
        reason: lastReason,
        attempts: RETRY_DELAYS_MS.length + 1,
      }
    );
  }

  throw new ContentApiError(USER_FACING_ERROR, lastStatus);
}

/**
 * Mock data is ONLY used during local development. In production a failure must
 * surface as an error (and be retried / reported), never silently replace the
 * real articles with fake placeholder posts.
 */
async function devMockPosts(): Promise<Post[] | null> {
  if (!import.meta.env.DEV) return null;
  const { mockPosts } = await import('@/api-client/mockPosts');
  return mockPosts;
}

export type PostsPage = {
  posts: Post[];
  /** Total number of matching posts (all pages) */
  total: number;
  skip: number;
  limit: number;
};

function mapPostsPage(data: any, skip: number, limit: number): PostsPage {
  const assets = buildAssetMap(data);
  const entries = buildEntryMap(data);
  const items = Array.isArray(data?.items) ? data.items : [];
  const posts = items.map((entry: any) => mapEntryToPost(entry, assets, entries));
  const total = typeof data?.total === 'number' ? data.total : skip + posts.length;
  return { posts, total, skip, limit };
}

function paginateMocks(posts: Post[], skip: number, limit: number): PostsPage {
  return { posts: posts.slice(skip, skip + limit), total: posts.length, skip, limit };
}

/** Optional server-side filter of a post list: one site section or one author. */
export type PostsFilter = { section?: CategorySlug; author?: string };

/** Dev-only approximation of the server-side filter (api/_lib/postFilters.ts): category or tag match. */
function matchesMockFilter(post: Post, { section, author }: PostsFilter): boolean {
  if (author) return post.author?.slug === author;
  if (section) {
    return [post.category, ...(post.tags ?? [])].some(
      (v) => getCategoryForPost(v)?.slug === section
    );
  }
  return true;
}

/** One page of the newest posts, optionally filtered (server-side pagination, for "load more" lists). */
export async function getPostsPage(
  skip = 0,
  limit = 12,
  filter: PostsFilter = {}
): Promise<PostsPage> {
  const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
  if (filter.section) params.set('section', filter.section);
  if (filter.author) params.set('author', filter.author);
  const url = `${API_BASE}/posts?${params}`;

  try {
    const data = await fetchApiJson(url, { context: 'getPostsPage', skip, limit, ...filter });
    return mapPostsPage(data, skip, limit);
  } catch (error) {
    const mocks = await devMockPosts();
    if (!mocks) throw error;
    return paginateMocks(
      mocks.filter((p) => matchesMockFilter(p, filter)),
      skip,
      limit
    );
  }
}

/** Full-text search across posts (Contentful `query=`). */
export async function searchPosts(query: string, skip = 0, limit = 12): Promise<PostsPage> {
  const url = `${API_BASE}/search?q=${encodeURIComponent(query)}&limit=${limit}&skip=${skip}`;

  try {
    const data = await fetchApiJson(url, { context: 'searchPosts', query, skip });
    return mapPostsPage(data, skip, limit);
  } catch (error) {
    const mocks = await devMockPosts();
    if (!mocks) throw error;
    const needle = query.toLowerCase();
    const matches = mocks.filter((p) =>
      [p.title, p.excerpt, p.subtitle, p.body, p.category, ...(p.tags ?? [])].some((v) =>
        v?.toLowerCase().includes(needle)
      )
    );
    return paginateMocks(matches, skip, limit);
  }
}

export async function getPostBySlug(slug: string): Promise<Post | null> {
  const url = `${API_BASE}/post-by-slug?slug=${encodeURIComponent(slug)}`;

  try {
    const data = await fetchApiJson(url, { context: 'getPostBySlug', slug });
    if (!data || !data.items || data.items.length === 0) {
      // Post not found (unpublished or deleted) - a successful API response with
      // 0 items means the content genuinely doesn't exist.
      return null;
    }

    const assets = buildAssetMap(data);
    const entries = buildEntryMap(data);
    return mapEntryToPost(data.items[0], assets, entries);
  } catch (error) {
    const mocks = await devMockPosts();
    if (!mocks) throw error;
    return mocks.find((p) => p.slug === slug) || null;
  }
}

/** Featured posts first by `featuredOrder` (posts without one last), then newest first. */
function compareFeatured(a: Post, b: Post): number {
  if (a.featuredOrder !== undefined && b.featuredOrder !== undefined) {
    return a.featuredOrder - b.featuredOrder;
  }
  if (a.featuredOrder !== undefined) return -1;
  if (b.featuredOrder !== undefined) return 1;
  return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
}

/** The post shown in the home page hero: the top featured post, or the newest post if none is featured. */
export async function getFeaturedPost(): Promise<Post | null> {
  let featured: Post[];
  try {
    const data = await fetchApiJson(`${API_BASE}/featured-posts?limit=100`, {
      context: 'getFeaturedPost',
    });
    featured = mapPostsPage(data, 0, 100).posts;
  } catch {
    // Already reported by fetchApiJson. The hero must not disappear because of the
    // featured list: fall back to the newest post below.
    const mocks = await devMockPosts();
    featured = mocks ? mocks.filter((p) => p.featured) : [];
  }

  if (featured.length > 0) return [...featured].sort(compareFeatured)[0];
  return (await getPostsPage(0, 1)).posts[0] ?? null;
}
